import ChevronLeft from "@mui/icons-material/ChevronLeft";
import EditIcon from "@mui/icons-material/Edit";
import { Button, Chip, Skeleton, Typography } from "@mui/material";
import { Box, Grid, Stack } from "@mui/system";
import { InfoBox } from "components/InfoBox";
import { ValueWithStaffEditedNotation } from "components/ValueWithStaffEditedNotation";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useProcessedManifestSuspense } from "hooks/useProcessedManifest";
import { useWasteLineUnifiedSuspense } from "hooks/useWasteLineUnified";
import { useCallback, useState } from "react";
import { formatNumber } from "util/formatNumber";
import { EditLineItemDialog } from "./EditLineItemDialog";

interface ManifestLineItemDetailSectionProps {
  processedManifestId: string | undefined;
  lineItemId: string | undefined;
  backButtonText: string;
  onBackButtonClick: () => void;
}

export const ManifestLineItemDetailSection = ({
  processedManifestId,
  lineItemId,
  backButtonText,
  onBackButtonClick,
}: ManifestLineItemDetailSectionProps) => {
  const { processedManifest } =
    useProcessedManifestSuspense(processedManifestId);
  const { lineItem } = useWasteLineUnifiedSuspense(lineItemId);
  const { isStaff } = useCurrentUser();
  const [showEditLineItemDialog, setShowEditLineItemDialog] = useState(false);

  const withAttribution = useCallback(
    (label: string) => {
      const showAttribution =
        processedManifest?.vendorManifest && processedManifest?.isEManifest;
      if (!showAttribution) {
        return label;
      }
      const suffix =
        lineItem?.vendorAmount != null ? "(Vendor)" : "(e‑Manifest)";
      return `${label} ${suffix}`;
    },
    [processedManifest, lineItem]
  );

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Button
          variant="text"
          size="large"
          startIcon={<ChevronLeft />}
          onClick={onBackButtonClick}
        >
          {backButtonText}
        </Button>
        {isStaff && (
          <Stack direction="row" alignItems="center" spacing={2}>
            {lineItem?.manuallyEdited && (
              <Typography
                variant="caption"
                color="text.disabled"
                fontStyle="italic"
              >
                edited
              </Typography>
            )}

            {lineItem?.vendorAmount != null && (
              <Button
                startIcon={<EditIcon />}
                color="warning"
                variant="text"
                size="large"
                onClick={() => setShowEditLineItemDialog(true)}
              >
                Edit Line Item
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      <Grid container spacing={2}>
        <Grid size={12}>
          <InfoBox
            text={lineItem?.lineItem}
            label={withAttribution("Line Item")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={
                  <>
                    {value}
                    {lineItem?.willNotReport && (
                      <Chip
                        variant="outlined"
                        label="Not Reportable"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </>
                }
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine
                    ?.vendorProfileDescription !== lineItem?.lineItem
                }
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <InfoBox
            text={lineItem?.dotDescription}
            label={withAttribution("DOT Description")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine?.dotPrintedInformation !==
                    lineItem?.dotDescription &&
                  lineItem?.originalVendorWasteLine?.nonHazWasteDescription !==
                    lineItem.dotDescription
                }
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={
              lineItem?.vendorAmount != null
                ? `${formatNumber(lineItem.vendorAmount)} lbs`
                : ""
            }
            label="Amount (Vendor)"
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine?.quantity !==
                    lineItem?.vendorAmount
                }
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={
              lineItem?.eManifestAmount != null
                ? `${formatNumber(lineItem.eManifestAmount)} lbs`
                : ""
            }
            label="Amount (e‑Manifest)"
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={lineItem?.profileNumber}
            label={withAttribution("Profile #")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine?.vendorProfileNumber !==
                    lineItem?.profileNumber
                }
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={lineItem?.managementMethodCode}
            label={withAttribution("Management Method Code")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine?.managementMethodCode !==
                    lineItem?.managementMethodCode
                }
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={lineItem?.formCode ?? ""}
            label={withAttribution("Form Code")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorFormCode !== lineItem?.formCode
                }
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <InfoBox
            text={lineItem?.sourceCode ?? ""}
            label={withAttribution("Source Code")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorSourceCode !== lineItem?.sourceCode
                }
              />
            )}
          />
        </Grid>
        <Grid size={12}>
          <InfoBox
            text={lineItem?.tags?.map((t) => t.name).join(", ")}
            label={withAttribution("Tags")}
            renderValue={(value) => (
              <ValueWithStaffEditedNotation
                value={value}
                isEdited={
                  !!lineItem?.manuallyEdited &&
                  lineItem?.originalVendorWasteLine?.tags
                    ?.map((t) => t.name)
                    .sort()
                    .join() !==
                    lineItem?.tags
                      .map((t) => t.name)
                      .sort()
                      .join()
                }
              />
            )}
          />
        </Grid>
      </Grid>

      {showEditLineItemDialog && lineItem && (
        <EditLineItemDialog
          lineItem={lineItem}
          onClose={() => setShowEditLineItemDialog(false)}
        />
      )}
    </>
  );
};

export const ManifestLineItemDetailSectionSkeleton = () => {
  return (
    <>
      <Box
        sx={{
          px: 3,
          py: 2,
          gap: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Skeleton variant="text" width={300} height={40} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="text" width={60} height={24} />
          <Skeleton variant="rectangular" width={120} height={36} />
          <Skeleton variant="circular" width={32} height={32} />
        </Stack>
      </Box>

      <Stack spacing={3} px={3} py={2} overflow="auto">
        <Skeleton variant="text" width={400} height={24} />

        <Grid container spacing={2}>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
          <Grid size={4}>
            <Skeleton variant="text" width={200} height={32} />
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};
